import React, { useState, useMemo, useEffect, useCallback } from 'react';
import QuestionCard from './QuestionCard';
import BoxDisplay from './BoxDisplay';
import CategoryFilter from './CategoryFilter';
import { leitnerService } from '../../services/leitnerService';

const Day = {
  Monday: 'Monday',
  Tuesday: 'Tuesday',
  Wednesday: 'Wednesday',
  Thursday: 'Thursday',
  Friday: 'Friday',
  Saturday: 'Saturday',
  Sunday: 'Sunday'
};

const LearnerView = ({ bookId, questions, updateLearnerProgress, styling }) => {
  const today = new Date().toLocaleString('en-US', { weekday: 'long' });
  const [selectedDay, setSelectedDay] = useState(today);
  const [sessionQuestions, setSessionQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sessionState, setSessionState] = useState('idle');
  
  const [timerMode, setTimerMode] = useState('none');
  const [timePerQuestion, setTimePerQuestion] = useState(60);
  const [timeForSession, setTimeForSession] = useState(300);
  const [timeLeft, setTimeLeft] = useState(0);
  const [finishReason, setFinishReason] = useState('completed');

  const [categories, setCategories] = useState({ subjects: [], grades: [], topics: [] });
  const [filters, setFilters] = useState({ subject: null, grade: null, topics: [] });
  const [filteredQuestions, setFilteredQuestions] = useState([]);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const cats = await leitnerService.getQuestionCategories(bookId);
        setCategories(cats);
      } catch (error) {
        console.error('Failed to load categories:', error);
      }
    };
    
    if (bookId) {
      loadCategories();
    }
  }, [bookId]);

  useEffect(() => {
    const filtered = leitnerService.filterQuestionsByCategories(questions, filters);
    setFilteredQuestions(filtered);
  }, [questions, filters]);

  // Flatten questions: simple questions and individual subquestions
  const flattenedQuestions = useMemo(() => {
    const flat = [];
    filteredQuestions.forEach(q => {
      if (q.type === 'simple') {
        flat.push({ ...q, isStem: false });
      } else if (q.type === 'stem' && q.subQuestions) {
        q.subQuestions.forEach(subQ => {
          flat.push({
            ...subQ,
            isStem: true,
            stemId: q.id,
            stemText: q.stemText,
            stemImage: q.stemImage
          });
        });
      }
    });
    return flat;
  }, [filteredQuestions]);

  const questionsByBox = useMemo(() => {
    return flattenedQuestions.reduce((acc, q) => {
      if (!acc[q.box]) {
        acc[q.box] = [];
      }
      acc[q.box].push(q);
      return acc;
    }, {});
  }, [flattenedQuestions]);
  
  useEffect(() => {
    setSessionState('idle');
    setSessionQuestions([]);
    setCurrentIndex(0);
  }, [selectedDay]);

  const startSession = () => {
    const box1 = questionsByBox[1] || [];
    const box2 = questionsByBox[2] || [];
    const box3 = questionsByBox[3] || [];
    
    let queue = [];
    if (selectedDay === Day.Friday) {
      queue = [...box3,...box1];
    } else if (selectedDay === Day.Tuesday || selectedDay === Day.Thursday) {
      queue = [...box2, ...box1];
    } else {
      queue = [...box1];
    }
    
    setSessionQuestions(queue);
    setCurrentIndex(0);
    setFinishReason('completed');

    if (timerMode === 'perQuestion') {
      setTimeLeft(timePerQuestion);
    } else if (timerMode === 'perSession') {
      setTimeLeft(timeForSession);
    }

    setSessionState(queue.length > 0 ? 'active' : 'finished');
  };

  const handleAnswerSubmit = useCallback(async (isCorrect) => {
    const currentQuestion = sessionQuestions[currentIndex];
    if (!currentQuestion) return;

    // Move to next question immediately for smooth UX
    if (currentIndex < sessionQuestions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      if (timerMode === 'perQuestion') {
        setTimeLeft(timePerQuestion);
      }
    } else {
      setSessionState('finished');
    }

    // Update progress in background (non-blocking)
    try {
      if (currentQuestion.isStem) {
        await updateLearnerProgress(currentQuestion.id, isCorrect, true, currentQuestion.stemId);
      } else {
        await updateLearnerProgress(currentQuestion.id, isCorrect, false);
      }
    } catch (error) {
      console.error('Background progress update failed:', error);
      // Don't interrupt user experience on error
    }
  }, [currentIndex, sessionQuestions, updateLearnerProgress, timerMode, timePerQuestion]);


  useEffect(() => {
    if (sessionState !== 'active' || timerMode === 'none') return;

    if (timeLeft <= 0) {
      if (timerMode === 'perQuestion') {
        handleAnswerSubmit(false);
      } else if (timerMode === 'perSession') {
        setFinishReason('timedOut');
        setSessionState('finished');
      }
      return;
    }

    const timerId = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timerId);
  }, [sessionState, timerMode, timeLeft, handleAnswerSubmit]);

  const currentQuestion = sessionState === 'active' ? sessionQuestions[currentIndex] : null;

  return (
    <div>
      <div className="mb-6">
        <CategoryFilter
          categories={categories}
          selectedFilters={filters}
          onFilterChange={setFilters}
          onClear={() => setFilters({ subject: null, grade: null, topics: [] })}
          questions={questions}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div id="session-setup" className="md:col-span-1 p-6 rounded-lg shadow-xl"
          style={{ backgroundColor: styling.headerColor }}
        >
          <h2 className="text-xl font-bold mb-4 text-indigo-400">Session Setup</h2>
          <label htmlFor="day-select" className="block text-sm font-medium text-slate-300 mb-2">
            Select Study Day
          </label>
          <select 
            id="day-select" 
            value={selectedDay} 
            onChange={(e) => setSelectedDay(e.target.value)} 
            disabled={sessionState === 'active'} 
            className="w-full bg-slate-700 border border-slate-600 rounded-md p-3 text-slate-100 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
          >
            {Object.values(Day).map(day => <option key={day} value={day}>{day}</option>)}
          </select>
          <p className="text-xs text-slate-400 mt-2">
            {selectedDay === Day.Friday && "Today's queue: Box 3 → 2 → 1"}
            {(selectedDay === Day.Tuesday || selectedDay === Day.Thursday) && "Today's queue: Box 2 → 1"}
            {![Day.Friday, Day.Tuesday, Day.Thursday].includes(selectedDay) && "Today's queue: Box 1"}
          </p>

          <div className="mt-6 border-t border-slate-700 pt-4">
            <h3 className="text-lg font-medium text-slate-300 mb-3">Timer Settings</h3>
            <div className="space-y-4">
              <div>
                <label htmlFor="timer-mode" className="block text-sm font-medium text-slate-400 mb-1">Mode</label>
                <select 
                  id="timer-mode" 
                  value={timerMode} 
                  onChange={e => setTimerMode(e.target.value)} 
                  disabled={sessionState === 'active'} 
                  className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                >
                  <option value="none">No Timer</option>
                  <option value="perQuestion">Per Question</option>
                  <option value="perSession">Entire Session</option>
                </select>
              </div>
              {timerMode === 'perQuestion' && (
                <div>
                  <label htmlFor="time-per-question" className="block text-sm font-medium text-slate-400 mb-1">
                    Seconds per question
                  </label>
                  <input 
                    type="number" 
                    id="time-per-question" 
                    value={timePerQuestion} 
                    onChange={e => setTimePerQuestion(Math.max(5, Number(e.target.value)))} 
                    min="5" 
                    className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 disabled:opacity-50" 
                    disabled={sessionState === 'active'} 
                  />
                </div>
              )}
              {timerMode === 'perSession' && (
                <div>
                  <label htmlFor="time-for-session" className="block text-sm font-medium text-slate-400 mb-1">
                    Total minutes for session
                  </label>
                  <input 
                    type="number" 
                    id="time-for-session" 
                    value={timeForSession / 60} 
                    onChange={e => setTimeForSession(Math.max(1, Number(e.target.value)) * 60)} 
                    min="1" 
                    className="w-full bg-slate-700 border border-slate-600 rounded-md p-2 text-slate-100 disabled:opacity-50" 
                    disabled={sessionState === 'active'} 
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div id="leitner-boxes" className="md:col-span-2">
          <BoxDisplay 
            questions={flattenedQuestions}
            styling={styling}
          />
        </div>
      </div>
      
      <div className="bg-slate-800 p-2 sm:p-2 rounded-lg shadow-xl min-h-[400px] flex flex-col items-center justify-center">
        {sessionState === 'idle' && (
          <div className="text-center">
            <h3 className="text-2xl font-semibold text-slate-200">Ready to learn?</h3>
            <p className="text-slate-400 mt-2 mb-6">Select a day and configure your session above.</p>
            <button 
              onClick={startSession} 
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-3 px-8 rounded-md transition-transform transform hover:scale-105 shadow-lg"
            >
              Start Session for {selectedDay}
            </button>
          </div>
        )}

        {sessionState === 'active' && currentQuestion && (
          <div className="w-full max-w-3xl">
            <div className="flex justify-between items-baseline text-slate-400 mb-4 px-2">
              <p>Question {currentIndex + 1} of {sessionQuestions.length} (from Box {currentQuestion.box})</p>
              {timerMode !== 'none' && (
                <div className={`font-bold text-lg tabular-nums rounded-md px-2 ${timeLeft <= 10 && timerMode === 'perQuestion' ? 'text-red-400 animate-pulse' : 'text-slate-300'}`}>
                  <span>{String(Math.floor(timeLeft / 60)).padStart(2, '0')}:{String(timeLeft % 60).padStart(2, '0')}</span>
                </div>
              )}
            </div>
            <QuestionCard
              key={currentQuestion.id}
              question={currentQuestion}
              onSubmit={handleAnswerSubmit}
              stemText={currentQuestion.isStem ? currentQuestion.stemText : undefined}
              stemImage={currentQuestion.isStem ? currentQuestion.stemImage : undefined}
            />
          </div>
        )}

        {sessionState === 'finished' && (
          <div className="text-center">
            {finishReason === 'timedOut' ? (
              <>
                <h3 className="text-2xl font-semibold text-amber-400">Time's Up!</h3>
                <p className="text-slate-300 mt-2 mb-6">The session timer has run out.</p>
              </>
            ) : (
              <>
                <h3 className="text-2xl font-semibold text-green-400">Session Complete!</h3>
                <p className="text-slate-300 mt-2 mb-6">Great work! You've reviewed all scheduled questions for today.</p>
              </>
            )}
            <button 
              onClick={() => setSessionState('idle')} 
              className="bg-slate-600 hover:bg-slate-700 text-white font-bold py-3 px-8 rounded-md transition"
            >
              Back to Setup
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LearnerView;